/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  PCD_GET_SCOPES,
  PCD_GET_SCOPES_SUCCESS,
  PCD_GET_SCOPES_ERROR,
  PCD_GET_PREFS,
  PCD_GET_PREFS_SUCCESS,
  PCD_GET_PREFS_ERROR,
  PCD_SAVE_PREFS,
  PCD_SAVE_PREFS_SUCCESS,
  PCD_SAVE_PREFS_ERROR,
  PCD_DELETE_PREFS,
  PCD_DELETE_PREFS_SUCCESS,
  PCD_DELETE_PREFS_ERROR,
  PCD_SET_CURRENT_SCOPE,
  PCD_SET_CURRENT_PAGE_KEY,
  PCD_CLEAR,
  prefsKey,
} from 'src/actions/pageColumnDefaultsAdminActions';

// CON-1532 Phase 9 — Page Column Defaults admin slice.
//
// Separate from the user-scoped page preferences flow so the admin path
// has zero blast radius on user flows.
//
// State shape:
//   scopes:           [{ key, label, companyId }]   — Generic + companies
//   currentCompanyId: number | null                  — selected scope (0 = Generic)
//   currentPageKey:   'CONTACTS' | 'EVENT_GUESTS' | 'GROUP_CONTACTS'
//   prefsByScope:     { [`${companyId}-${pageKey}`]: { version, columns, sortBy } | null }
//   scopesLoading:    boolean
//   loading:          boolean   — true while the current scope+pageKey
//                                 is being fetched / saved / deleted
//   error:            string | null

const initialState = {
  scopes: [],
  currentCompanyId: 0, // Generic default selected on first load
  currentPageKey: 'CONTACTS',
  prefsByScope: {},
  scopesLoading: false,
  loading: false,
  error: null,
};

const pageColumnDefaultsAdminReducer = (state = initialState, action) => {
  switch (action.type) {
    // ── scopes ───────────────────────────────────────────────────────
    case PCD_GET_SCOPES:
      return produce(state, (draft) => {
        draft.scopesLoading = true;
        draft.error = null;
      });
    case PCD_GET_SCOPES_SUCCESS:
      return produce(state, (draft) => {
        draft.scopesLoading = false;
        draft.scopes = action.payload.scopes;
      });
    case PCD_GET_SCOPES_ERROR:
      return produce(state, (draft) => {
        draft.scopesLoading = false;
      });

    // ── prefs (per scope+pageKey) ───────────────────────────────────
    case PCD_GET_PREFS:
      return produce(state, (draft) => {
        draft.loading = true;
        draft.error = null;
      });
    case PCD_GET_PREFS_SUCCESS:
      return produce(state, (draft) => {
        draft.loading = false;
        draft.prefsByScope[prefsKey(action.payload.companyId, action.payload.pageKey)] = action.payload.preferences;
      });
    case PCD_GET_PREFS_ERROR:
      return produce(state, (draft) => {
        draft.loading = false;
      });

    // ── save ─────────────────────────────────────────────────────────
    case PCD_SAVE_PREFS:
      return produce(state, (draft) => {
        draft.loading = true;
        draft.error = null;
      });
    case PCD_SAVE_PREFS_SUCCESS:
      return produce(state, (draft) => {
        draft.loading = false;
      });
    case PCD_SAVE_PREFS_ERROR:
      return produce(state, (draft) => {
        draft.loading = false;
      });

    // ── delete ───────────────────────────────────────────────────────
    case PCD_DELETE_PREFS:
      return produce(state, (draft) => {
        draft.loading = true;
        draft.error = null;
      });
    case PCD_DELETE_PREFS_SUCCESS:
      return produce(state, (draft) => {
        draft.loading = false;
      });
    case PCD_DELETE_PREFS_ERROR:
      return produce(state, (draft) => {
        draft.loading = false;
      });

    // ── UI selection ─────────────────────────────────────────────────
    case PCD_SET_CURRENT_SCOPE:
      return produce(state, (draft) => {
        draft.currentCompanyId = action.payload.companyId;
      });
    case PCD_SET_CURRENT_PAGE_KEY:
      return produce(state, (draft) => {
        draft.currentPageKey = action.payload.pageKey;
      });

    // ── reset ────────────────────────────────────────────────────────
    case PCD_CLEAR:
      return initialState;

    default:
      return state;
  }
};

export default pageColumnDefaultsAdminReducer;
